import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { BlueprintDrawer } from '../../modules/excelencia/components/drawer/BlueprintDrawer';
import { ExecutionDrawerHost } from '../../modules/execution/components/ExecutionDrawerHost';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';

export type DrawerModule = 'execution' | 'excelencia';

export interface DrawerHostState {
  module: DrawerModule | null;
  entityId: string | null;
  mode?: string | null;
}

interface DrawerOpenPayload {
  module: DrawerModule;
  entityId: string;
  mode?: string | null;
}

interface DrawerRendererProps {
  entityId: string;
  organizationId: string;
  mode?: string | null;
  onClose: () => void;
}

type DrawerRenderer = (props: DrawerRendererProps) => ReactNode;

interface DrawerHostContextValue {
  state: DrawerHostState;
  openDrawer: (payload: DrawerOpenPayload) => void;
  closeDrawer: () => void;
}

const DrawerHostContext = createContext<DrawerHostContextValue>({
  state: { module: null, entityId: null, mode: null },
  openDrawer: () => {},
  closeDrawer: () => {},
});

const MODULE_RENDERERS: Partial<Record<DrawerModule, DrawerRenderer>> = {
  excelencia: ({ entityId, organizationId, onClose, mode }) => (
    <BlueprintDrawer
      open
      blueprintId={entityId}
      organizationId={organizationId}
      onClose={onClose}
      mode={mode}
      withinHost
    />
  ),
  execution: ({ entityId, organizationId, onClose, mode }) => (
    <ExecutionDrawerHost entityId={entityId} organizationId={organizationId} mode={mode} onClose={onClose} />
  ),
};

export function DrawerHostProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DrawerHostState>({ module: null, entityId: null, mode: null });

  const closeDrawer = useCallback(() => {
    setState({ module: null, entityId: null, mode: null });
  }, []);

  const openDrawer = useCallback((payload: DrawerOpenPayload) => {
    setState({
      module: payload.module,
      entityId: payload.entityId,
      mode: payload.mode ?? null,
    });
  }, []);

  const value = useMemo(() => ({ state, openDrawer, closeDrawer }), [state, openDrawer, closeDrawer]);

  return (
    <DrawerHostContext.Provider value={value}>
      {children}
      <AppDrawerHost state={state} onClose={closeDrawer} />
    </DrawerHostContext.Provider>
  );
}

export function useDrawerHost() {
  return useContext(DrawerHostContext);
}

function AppDrawerHost({ state, onClose }: { state: DrawerHostState; onClose: () => void }) {
  const { workspace } = useWorkspace();
  const { user } = useAuth();
  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  const organizationId = workspace?.organizationId ?? user?.organizationId ?? null;

  if (!portalTarget || !state.module || !state.entityId || !organizationId) {
    return null;
  }

  const Renderer = MODULE_RENDERERS[state.module];
  if (!Renderer) {
    return null;
  }

  const content = (
    <div className="fixed inset-0 z-[70] flex">
      <button type="button" aria-label="Fechar painel lateral" className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="enterprise-side-panel relative ml-auto flex h-full flex-col">
        {Renderer({ entityId: state.entityId, organizationId, onClose, mode: state.mode })}
      </div>
    </div>
  );

  return createPortal(content, portalTarget);
}
